import { IS_V53_ENABLED_ENV } from '@portal/shared/constants/featureFlags';
import {
  ALL_PRODUCTS_CATEGORY_TAB,
  BUNDLES_CATEGORY_TAB
} from '@portal/shared/studio/ShopListNav/constants';
import { RefObject } from 'react';
import { DESKTOP_SECTION_TOP_OFFSET, MOBILE_SECTION_TOP_OFFSET } from './constants';
import {
  GenericLoaderKey,
  GenericRefNames,
  MembersOnlyLoaderKey,
  MembersOnlyRefName,
  ProductListStoreState,
  ShopV3CartStoreState
} from './types';

const checkIsPageLoaded = (state: ProductListStoreState, options: { isLoggedIn: boolean }) => {
  const {
    loading: { isBundlesSectionLoading, isProductsSectionLoading, membersOnly },
    pageRefs: { hasMembersOnlySections }
  } = state.data;

  if (IS_V53_ENABLED_ENV && options.isLoggedIn && hasMembersOnlySections) {
    const isMembersOnlySectionsLoading = Object.keys(membersOnly).some(
      (loaderKey: MembersOnlyLoaderKey) => membersOnly[loaderKey] === true
    );
    return !isBundlesSectionLoading && !isProductsSectionLoading && !isMembersOnlySectionsLoading;
  }
  return !isBundlesSectionLoading && !isProductsSectionLoading;
};

const updateSectionLoadingState = (
  state: ProductListStoreState,
  options: { isLoggedIn: boolean }
) => {
  const newState = { ...state };
  newState.data.loading.isPageLoaded = checkIsPageLoaded(newState, options);
  if (newState.data.loading.isLoadingCategoryPage && newState.data.loading.isPageLoaded) {
    newState.data.loading.isLoadingCategoryPage = false;
  }
  return newState;
};

export const setIsGenericSectionLoading = (
  state: ProductListStoreState,
  loaderKey: GenericLoaderKey,
  newValue: boolean,
  options: { isLoggedIn: boolean }
) => {
  const newState = { ...state };
  newState.data.loading[loaderKey] = newValue;
  return updateSectionLoadingState(newState, options);
};

export const setIsMembersOnlySectionLoading = (
  state: ProductListStoreState,
  loaderKey: MembersOnlyLoaderKey,
  newValue: boolean,
  options: { isLoggedIn: boolean }
) => {
  const newState = { ...state };
  newState.data.loading.membersOnly[loaderKey] = newValue;
  return updateSectionLoadingState(newState, options);
};

export const setGenericSectionRef = (
  state: ProductListStoreState,
  sectionRefName: GenericRefNames,
  ref: RefObject<HTMLDivElement | null>
) => {
  const newState = { ...state };

  const currentRef = newState.data.pageRefs[sectionRefName];
  if (currentRef === undefined || currentRef.current === null) {
    newState.data.pageRefs[sectionRefName] = ref;
  }
  return newState;
};

export const setMembersOnlySectionRef = (
  state: ProductListStoreState,
  sectionRefName: MembersOnlyRefName,
  ref: RefObject<HTMLDivElement | null>
) => {
  const newState = { ...state };
  const currentRef = newState.data.pageRefs.membersOnly[sectionRefName];
  if (currentRef === undefined || currentRef.current === null) {
    newState.data.pageRefs.membersOnly[sectionRefName] = ref;
  }
  return newState;
};

export const scrollToSectionTop = (
  state: ProductListStoreState,
  options: {
    aboveSmall: boolean;
    isMembersOnlyProductSection?: boolean;
    refName?: MembersOnlyRefName;
  }
) => {
  const {
    currentActiveCategoryTab: { slug },
    pageRefs: { bannersSectionRef, bundlesSectionRef, productsSectionRef, membersOnly }
  } = state.data;

  let sectionRef = productsSectionRef?.current;

  if (slug === ALL_PRODUCTS_CATEGORY_TAB) {
    sectionRef =
      bannersSectionRef && bannersSectionRef?.current
        ? bannersSectionRef?.current
        : bundlesSectionRef?.current;
  } else if (slug === BUNDLES_CATEGORY_TAB) {
    sectionRef = bundlesSectionRef?.current;
  } else if (options.isMembersOnlyProductSection && options.refName) {
    sectionRef = membersOnly[options.refName]?.current;
  }

  if (sectionRef) {
    const sectionTop = sectionRef?.offsetTop;
    if (sectionTop) {
      window.scroll({
        top:
          sectionTop -
          (options.aboveSmall ? DESKTOP_SECTION_TOP_OFFSET : MOBILE_SECTION_TOP_OFFSET),
        behavior: 'smooth'
      });
    }
  }
};

// Cart Utils
export const setCartLoading = (
  state: ShopV3CartStoreState,
  loaderKey: keyof ShopV3CartStoreState['loading'],
  newValue: boolean
) => {
  const newState = { ...state };
  newState.loading[loaderKey] = newValue;
  return newState;
};

// Cart Utils
export const setCartError = (
  state: ShopV3CartStoreState,
  loaderKey: keyof ShopV3CartStoreState['errors'],
  newValue: Error
) => {
  const newState = { ...state };
  newState.errors[loaderKey] = newValue;
  return newState;
};
